import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/api/client";
import { MapPin, Loader2, Check } from "lucide-react";

/**
 * Pincode input with India Post lookup. Auto-fills city/state when a valid
 * 6-digit pincode is entered. Debounced.
 * Props: value, onChange(pincode), onResolved({city, state, areas})
 */
export default function PincodeInput({ value, onChange, onResolved, testid = "pincode-input", placeholder = "6-digit PIN" }) {
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|loading|ok|err
  const debounceRef = useRef();

  useEffect(() => {
    const pc = (value || "").replace(/\D/g, "");
    if (pc.length !== 6) { setInfo(null); setStatus("idle"); return; }
    setStatus("loading");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pincode/${pc}`);
        if (data.found) {
          setInfo(data); setStatus("ok");
          onResolved && onResolved(data);
        } else {
          setStatus("err"); setInfo(null);
        }
      } catch { setStatus("err"); setInfo(null); }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [value]); // eslint-disable-line

  return (
    <div>
      <div className="relative">
        <Input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="rounded-md font-mono pr-9"
          placeholder={placeholder}
          maxLength={6}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {status === "loading" && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
          {status === "ok" && <Check size={14} className="text-primary" />}
          {status === "err" && <MapPin size={14} className="text-destructive" />}
        </div>
      </div>
      {info && (
        <div data-testid={`${testid}-info`} className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1.5">
          <MapPin size={11} className="text-primary" />
          <span className="text-foreground font-medium">{info.city}</span>
          <span>· {info.state}</span>
          {info.areas?.[0]?.name && <span>· {info.areas[0].name}</span>}
        </div>
      )}
    </div>
  );
}
